/**
 * Role-based constants for PolicyLens.
 * Mirrors the enum definitions from the reference app.
 */

// ── User Roles ───────────────────────────────────────────────
export const Role = Object.freeze({
    ADMIN: 'ADMIN',
    LEAD_ADVISOR: 'LEAD_ADVISOR',
    ADVISOR: 'ADVISOR',
    COMPANY_SPOC: 'COMPANY_SPOC',
    COMPANY_DELEGATE: 'COMPANY_DELEGATE',
    /** Prolense support staff — backend role `support_agent` (support console only). */
    SUPPORT_AGENT: 'SUPPORT_AGENT',
});

// ── Application Views ────────────────────────────────────────
export const View = Object.freeze({
    LANDING: 'LANDING',
    SIGN_IN: 'SIGN_IN',
    DASHBOARD: 'DASHBOARD',
    /** Platform admin — invite & roster for Lead Advisors */
    LEAD_ADVISORS: 'LEAD_ADVISORS',
    ADMINISTRATORS: 'ADMINISTRATORS',
    /** Lead Advisor's own firm profile — onboarding wizard (no AdvisoryFirm row yet) or
     *  settings/edit (row exists). See .claude/design/la-onboarding-razorpay.md Workstream A. */
    ADVISORY_FIRM: 'ADVISORY_FIRM',
    /** Lead Advisor's own third-party credentials (LinkedIn) — /me/integrations/credentials. */
    INTEGRATIONS: 'INTEGRATIONS',
    /** platform_admin-only platform feed keys — /admin/platform-credentials. */
    PROJECT_WORKSPACE: 'PROJECT_WORKSPACE',
    PROJECT_MEMBERS: 'PROJECT_MEMBERS',
    /** Persona-adaptive project dashboard + Company-SPOC shareable-link entry point.
     *  See .claude/design/dashboard-redesign.md ("Frontend additions"). */
    PROJECT_DASHBOARD: 'PROJECT_DASHBOARD',
    TASK_CENTER: 'TASK_CENTER',
    QUESTION_BANK: 'QUESTION_BANK',
    POLICY_LIBRARY: 'POLICY_LIBRARY',
    EVIDENCE: 'EVIDENCE',
    DOCUMENT_LIBRARY: 'DOCUMENT_LIBRARY',
    MASTER_DATA: 'MASTER_DATA',
    VULNERABILITY_MASTER: 'VULNERABILITY_MASTER',
    MEETINGS: 'MEETINGS',
    /** Prolense support tickets — platform_admin console / LA + SPOC own tickets (SupportPage.jsx). */
    SUPPORT: 'SUPPORT',
    /** Project-scoped Software QA results — Lead Advisor / Advisor (SqaResultsPage.jsx). */
    SQA_RESULTS: 'SQA_RESULTS',
    /** Project training (Sheet-2 row 9) — Advisor/LA authoring + history, SPOC/Delegate "My training" (TrainingPage.jsx). */
    TRAINING: 'TRAINING',
    /** Policy Hub template library (Sheet-2 row 2) — platform_admin curator console / Advisor+LA project lookup (LibraryTemplatesPage.jsx). */
    LIBRARY_TEMPLATES: 'LIBRARY_TEMPLATES',
    /** Question Bank curator (Sheet-2 row 3) - platform_admin only: AI-assisted drafting, review, revise/retire, publish (QuestionBankCuratorPage.jsx). */
    QUESTION_BANK_CURATOR: 'QUESTION_BANK_CURATOR',
    /** Regulatory Database master (Sheet-2 rows 8 + 10) - platform_admin only (RegulatoryDatabasePage.jsx). */
    REGULATORY_DATABASE: 'REGULATORY_DATABASE',
    /** Read-only regulatory library - Lead Advisor / Advisor / Company SPOC (RegulatoryLibraryPage.jsx). */
    REGULATORY_LIBRARY: 'REGULATORY_LIBRARY',
    /** Project-level 'Which authorities apply?' - Lead Advisor / Advisor (ProjectRegulationsPage.jsx). */
    PROJECT_REGULATIONS: 'PROJECT_REGULATIONS',
});

// ── Project Status / Phase ───────────────────────────────────
export const ProjectStatus = Object.freeze({
    ONBOARDING: 'Onboarding',
    GAP_ANALYSIS: 'Gap Analysis',
    RISK_ASSESSMENT: 'Risk Assessment',
    IMPLEMENTATION: 'Implementation',
    AUDIT: 'Audit',
    COMPLETED: 'Completed',
});

// ── Role → backend value mapping ─────────────────────────────
// These are the role IDs used in the Azure AD access token
export const ROLE_TOKEN_MAP = {
    platform_admin: Role.ADMIN,
    lead_advisor: Role.LEAD_ADVISOR,
    advisor: Role.ADVISOR,
    company_spoc: Role.COMPANY_SPOC,
    company_delegate: Role.COMPANY_DELEGATE,
    support_agent: Role.SUPPORT_AGENT,
};
